import {
  Box,
  Stack,
  Grid2 as Grid,
  Button,
  Typography,
  Switch,
} from '@mui/material';
import storeConnector from '@src/store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';
import useSubscription from '@src/hooks/useSubscription';
import { ControlModes } from '@src/types/Stacks';
import EditIcon from '@mui/icons-material/Edit';
import { useState, useCallback, useMemo } from 'react';
import TemplatesFields from './TemplatesFields';
import { useForm, FormProvider } from 'react-hook-form';
import cloneDeep from 'lodash.clonedeep';
import TemplatesEnableConfirm from './TemplatesEnableConfirm';
import TemplatesFullConfig from './TemplatesFullConfig';
import { templatesViewSx } from '@src/components/_shared/sxStyles/Config/templates';
import { getQuickConfigFields } from '../../utils';
import { useConfigContext } from '@src/pages/Home/pages/Config/ConfigWrapperContext';
import TemplatesCardStatuses from './TemplatesCardStatuses';
import { generateDataPoint } from '@src/utils/generateTopic';
import { ControlAuthorities } from '@src/types/ControlModes';

interface TemplatesCardProps {
  controlMode: ControlModes;
  siteMeta: SiteMeta;
  handleUpdateStack: (controlMode: ControlModes) => void;
  isDefault?: boolean;
}

export interface FieldState {
  [fieldName: string]: {
    value: string | number | boolean;
    type: string;
    quickConfig?: boolean;
  };
}

export type FormValues = {
  [fieldName: string]: string | number | boolean;
};

function TemplatesCard({
  controlMode,
  siteMeta,
  handleUpdateStack,
  isDefault,
}: TemplatesCardProps) {
  const defaultValues: FormValues = {};
  const { controlAuthority } = useConfigContext();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  controlMode.sections.forEach((section) => {
    if (section.fields) {
      section.fields.forEach((field) => {
        const fieldName = field.controllerField;
        defaultValues[fieldName] = field.value;
      });
    }
  });

  const quickConfigFields = useMemo(
    () => getQuickConfigFields(controlMode),
    [controlMode],
  );

  const statusTypes = useMemo(
    () => [
      {
        key: 'scada',
        pointName: `Disable_${controlMode.name}_SCADA`,
        displayName: `${controlMode.name} SCADA`,
        tooltipMessage: `Mode status that is set by SCADA commands. ${controlAuthority === ControlAuthorities.scada ? 'The authority is set to SCADA. The status can be enabled/disabled only from the customer SCADA system.' : `The authority is set to ${controlAuthority}. Switch to SCADA authority to enable/disable modes from the customer SCADA system.`}`,
      },
      {
        key: 'hmi',
        pointName: `Disable_${controlMode.name}_Fractal`,
        displayName: `${controlMode.name} HMI`,
        tooltipMessage: `Mode status that is set by the HMI user. ${controlAuthority === ControlAuthorities.hmi ? 'The authority is set to HMI. The status can be enabled/disabled from this page.' : `The authority is set to ${controlAuthority}. Switch to HMI authority to enable/disable modes from this page.`}`,
      },
      {
        key: 'active',
        pointName: `Disable_${controlMode.name}`,
        displayName: `${controlMode.name} Disable`,
        tooltipMessage: `Current run status of the control mode. The authority is set to ${controlAuthority} and the status is set by ${controlAuthority} Status.`,
      },
    ],
    [controlMode.name, controlAuthority],
  );

  const subscriptionConfig = useMemo(
    () =>
      statusTypes.map(({ pointName }) =>
        generateDataPoint(
          {
            deviceType: 'site',
            controllerId: siteMeta.siteId,
            pointName,
            dataType: 'integer',
          },
          siteMeta.siteId,
        ),
      ),
    [statusTypes, siteMeta.siteId],
  );

  const subscriptionsWithValues = useSubscription(subscriptionConfig);

  const { scadaStatus, activeStatus, hmiStatus } = useMemo(() => {
    const result: Record<string, string | number | null | undefined> = {};
    statusTypes.forEach(({ key, pointName }) => {
      const subscription = subscriptionsWithValues.find(
        (s) => s.pointName === pointName,
      );
      result[`${key}Status`] = subscription?.value;
    });
    return result as {
      scadaStatus: string | number | null | undefined;
      activeStatus: string | number | null | undefined;
      hmiStatus: string | number | null | undefined;
    };
  }, [statusTypes, subscriptionsWithValues]);

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    resetOptions: {
      keepDirtyValues: false,
    },
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const mapFormValuesToControlMode = useCallback(
    (formValues: FormValues) => {
      const updatedControlMode = cloneDeep(controlMode);

      updatedControlMode.sections.forEach((section) => {
        if (section.fields) {
          section.fields.forEach((field) => {
            const fieldName = field.controllerField;
            if (fieldName in formValues) {
              field.value =
                field.type === 'boolean'
                  ? formValues[fieldName] === 1
                    ? true
                    : false
                  : formValues[fieldName];
            }
          });
        }
      });

      return updatedControlMode;
    },
    [controlMode],
  );

  const onSubmit = useCallback(
    (formValues: FormValues) => {
      const updatedControlMode = mapFormValuesToControlMode(formValues);
      handleUpdateStack(updatedControlMode);
      setOpen(false);
    },
    [mapFormValuesToControlMode, handleUpdateStack],
  );

  const handleToggleUpdate = (_: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmOpen(true);
  };

  return (
    <FormProvider {...methods}>
      <Grid container spacing={2}>
        <Grid size={4} sx={templatesViewSx.templateNameGrid}>
          <Typography sx={templatesViewSx.templateName}>
            {controlMode.stackAlias ?? controlMode.name}
          </Typography>
          {siteMeta.ui.features?.ScadaConfig_Enabled && isDefault && (
            <TemplatesCardStatuses
              scadaStatus={scadaStatus}
              activeStatus={activeStatus}
              hmiStatus={hmiStatus}
              statusTypes={statusTypes}
            />
          )}
        </Grid>
        <Grid size={8}>
          <Stack
            direction={'row'}
            sx={templatesViewSx.configWrapper}
            spacing={2}
          >
            <Box display={'flex'} alignItems='center'>
              <Switch
                checked={activeStatus === 0}
                onChange={handleToggleUpdate}
                disabled={controlAuthority === ControlAuthorities.scada}
              />
            </Box>
            <Stack spacing={3} flexGrow={1}>
              <TemplatesFields inputFields={quickConfigFields} />
              <Stack
                direction='row'
                spacing={2}
                sx={templatesViewSx.configWrapper.buttonWrapper}
              >
                {quickConfigFields.length > 0 && (
                  <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    type='submit'
                    onClick={methods.handleSubmit(onSubmit)}
                  >
                    Quick Save
                  </Button>
                )}
                <Button
                  variant='outlined'
                  color='inherit'
                  size='small'
                  startIcon={<EditIcon />}
                  onClick={handleOpen}
                >
                  Full Config
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        {open && (
          <TemplatesFullConfig
            setOpen={setOpen}
            controlMode={controlMode}
            onSubmit={onSubmit}
          />
        )}

        {confirmOpen && (
          <TemplatesEnableConfirm
            setConfirmOpen={setConfirmOpen}
            modeName={controlMode.name}
            activeStatus={activeStatus}
          />
        )}
      </Grid>
    </FormProvider>
  );
}

export default storeConnector(TemplatesCard, {
  config: ['siteMeta'],
});
